/**
 * Login page component.
 *
 * Flow: Email → method selection (passkey or password) → MFA if needed.
 *
 * Supports mobile login flow via ?mobile=true&callback=<url> parameters.
 * When mobile=true, after successful login a mobile token is fetched
 * and the user is redirected to the callback URL with the token.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { LoginFlow, UiNodeInputAttributes } from '@ory/client';
import { kratos, getFlowId, getReturnTo, redirectTo, handleKratosError } from '../lib/kratos';
import { useAuth } from '../context/AuthContext';
import FormField from '../components/FormField';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import QRLogin from '../components/QRLogin';

type LoginPhase = 'identifier' | 'password' | 'webauthn' | 'totp' | 'lookup_secret';
type LoginTab = 'credentials' | 'qr';

import { config } from '../config';
// Gateway URL for mobile token exchange (must match config.gatewayUrl)
const GATEWAY_URL = config.gatewayUrl;

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('identifier');
  const [totpCode, setTotpCode] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [hasPasskey, setHasPasskey] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [loginTab, setLoginTab] = useState<LoginTab>('credentials');

  // Check if this is a mobile login request
  const params = new URLSearchParams(location.search);
  const isMobileLogin = params.get('mobile') === 'true';
  const mobileCallback = params.get('callback');
  const forceRefresh = params.get('refresh') === 'true';
  const requestedAal = params.get('aal');

  // Capture returnTo once from the initial URL — do NOT recompute on every
  // location.search change, or the useCallback/useEffect chain will loop.
  const returnToRef = useRef<string | null>(null);
  if (returnToRef.current === null) {
    returnToRef.current = isMobileLogin && mobileCallback
      ? `${window.location.origin}/mobile-token-exchange?callback=${encodeURIComponent(mobileCallback)}`
      : getReturnTo(location.search);
  }
  const returnTo = returnToRef.current;

  const detectLoginPhase = (flowData: LoginFlow): LoginPhase => {
    const totpNodes = flowData.ui.nodes.filter((n) => n.group === 'totp');
    if (totpNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'totp_code')) {
      return 'totp';
    }

    const webauthnNodes = flowData.ui.nodes.filter((n) => n.group === 'webauthn');
    const hasWebauthnTrigger = webauthnNodes.some((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'webauthn_login_trigger';
    });

    if (hasWebauthnTrigger) {
      setHasPasskey(true);
      return 'webauthn';
    }

    const lookupNodes = flowData.ui.nodes.filter((n) => n.group === 'lookup_secret');
    if (lookupNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'lookup_secret')) {
      return 'lookup_secret';
    }

    // If webauthn method node exists (but no trigger yet), start with identifier
    const hasWebauthnMethod = webauthnNodes.length > 0;
    if (hasWebauthnMethod && !identifier) {
      return 'identifier';
    }

    return 'password';
  };

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        try {
          const { data } = await kratos.getLoginFlow({ id: flowId });
          setFlow(data);
          setLoginPhase(detectLoginPhase(data));
          return;
        } catch (flowErr) {
          // 403 = CSRF mismatch (stale cookies after Kratos restart).
          // Clear Kratos cookies and reload once to break the loop.
          const resp = (flowErr as { response?: { status?: number } })?.response;
          if (resp?.status === 403) {
            document.cookie.split(';').forEach((c) => {
              const name = c.split('=')[0].trim();
              if (name.startsWith('csrf_token') || name === 'ory_kratos_session' || name === 'passport_session') {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname.split('.').slice(-2).join('.')}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
              }
            });
            // Reload once — clean cookies will let the new flow work
            window.location.replace(window.location.href);
            return;
          }
          // Other errors (expired flow, etc.) — create fresh
        }
      }

      const { data } = await kratos.createBrowserLoginFlow({
        returnTo,
        refresh: forceRefresh,
        aal: requestedAal || undefined,
      });
      const newParams = new URLSearchParams();
      newParams.set('flow', data.id);
      if (returnTo && returnTo !== '/organizations') {
        newParams.set('return_to', returnTo);
      }
      if (isMobileLogin) {
        newParams.set('mobile', 'true');
        if (mobileCallback) newParams.set('callback', mobileCallback);
      }
      navigate(`/login?${newParams.toString()}`, { replace: true });
      setFlow(data);
      setLoginPhase(detectLoginPhase(data));
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate, isMobileLogin, mobileCallback, forceRefresh, requestedAal]);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && !forceRefresh && !requestedAal) {
      if (redirecting) return;
      setRedirecting(true);
      if (isMobileLogin && mobileCallback) {
        fetchMobileToken(mobileCallback);
        return;
      }
      redirectTo(returnTo, navigate);
      return;
    }
    initializeFlow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeFlow, isAuthenticated, authLoading, navigate, isMobileLogin, mobileCallback, forceRefresh, requestedAal]);

  // ============================================================
  // Helpers
  // ============================================================

  const getCsrfToken = (): string | undefined => {
    const node = flow?.ui.nodes.find((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'csrf_token';
    });
    return node ? (node.attributes as UiNodeInputAttributes).value as string : undefined;
  };

  const handleLoginSuccess = async (data: { session?: unknown }) => {
    if (data.session) {
      if (isMobileLogin && mobileCallback) {
        await fetchMobileToken(mobileCallback);
        return;
      }
      window.location.href = returnTo;
    }
  };

  const handle422Redirect = (err: unknown): boolean => {
    if (err && typeof err === 'object' && 'response' in err) {
      const response = (err as { response?: { status?: number; data?: { redirect_browser_to?: string } } }).response;
      if (response?.status === 422 && response?.data?.redirect_browser_to) {
        const redirectUrl = response.data.redirect_browser_to;
        const url = new URL(redirectUrl);

        // Extract the flow ID from the redirect URL — Kratos already created the flow
        const redirectFlowId = url.searchParams.get('flow');
        const aal = url.searchParams.get('aal');

        (async () => {
          try {
            if (redirectFlowId) {
              // Fetch the existing flow — it should have the webauthn challenge
              const { data } = await kratos.getLoginFlow({ id: redirectFlowId });

              const newParams = new URLSearchParams();
              newParams.set('flow', redirectFlowId);
              if (isMobileLogin) {
                newParams.set('mobile', 'true');
                if (mobileCallback) newParams.set('callback', mobileCallback);
              }
              navigate(`/login?${newParams.toString()}`, { replace: true });

              setFlow(data);
              const phase = detectLoginPhase(data);
              setLoginPhase(phase);
            } else if (aal) {
              // AAL upgrade — create new flow
              const newParams = new URLSearchParams();
              newParams.set('aal', aal);
              navigate(`/login?${newParams.toString()}`, { replace: true });
            }
          } catch {
            // Fallback — navigate to the redirect URL directly
            window.location.href = redirectUrl;
          } finally {
            setIsSubmitting(false);
          }
        })();
        return true;
      }
    }
    return false;
  };

  const fetchMobileToken = async (callback: string): Promise<void> => {
    try {
      const response = await fetch(`${GATEWAY_URL}/v1/auth/mobile-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Device' : 'Unknown Device',
        }),
      });
      if (!response.ok) throw new Error('Failed to get mobile token');
      const data = await response.json();
      const params = new URLSearchParams();
      params.set('token', data.token);
      if (data.user) {
        try { params.set('user', btoa(JSON.stringify(data.user))); } catch { /* skip */ }
      }
      const isCustomScheme = callback.includes('://') && !callback.startsWith('http');
      if (isCustomScheme) {
        window.location.href = `${callback}?${params.toString()}`;
        return;
      }
      let path = '/mobile-callback';
      try { path = new URL(callback).pathname; } catch { /* default */ }
      navigate(`${path}?${params.toString()}`, { replace: true });
    } catch (err) {
      console.error('fetchMobileToken error:', err);
      setError('Failed to generate mobile token. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: Identifier — submit email to Kratos with method=webauthn
  // If user has passkey, Kratos returns 422 → new flow with challenge
  // If not, we fall through to password
  // ============================================================

  const handleIdentifierSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const email = (event.currentTarget.querySelector('input[name="identifier"]') as HTMLInputElement)?.value
      || (event.currentTarget.querySelector('input[type="email"]') as HTMLInputElement)?.value
      || identifier;

    if (!email) {
      setError('Please enter your email address');
      setIsSubmitting(false);
      return;
    }

    setIdentifier(email);

    try {
      // Try webauthn method first — if user has a passkey, Kratos will return 422 redirect
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'webauthn',
          identifier: email,
          csrf_token: getCsrfToken(),
        } as unknown as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });

      // If we get here without error, login succeeded (unlikely for identifier-only)
      await handleLoginSuccess(data);
    } catch (err) {
      // 422 = Kratos wants browser redirect → new flow with webauthn challenge
      if (handle422Redirect(err)) return;

      // 400 = no webauthn credential for this user → fall to password
      setLoginPhase('password');
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: Password submit
  // ============================================================

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => { values[key] = value.toString(); });

    // Ensure identifier is set
    if (!values.identifier && identifier) {
      values.identifier = identifier;
    }

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: values as unknown as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });

      if ('session' in data && data.session) {
        await handleLoginSuccess(data);
        return;
      }

      // MFA required
      const flowData = data as unknown as LoginFlow;
      if ('ui' in data) {
        const newPhase = detectLoginPhase(flowData);
        if (newPhase !== 'password' && newPhase !== 'identifier') {
          setFlow(flowData);
          setLoginPhase(newPhase);
          setIsSubmitting(false);
          return;
        }
        setFlow(flowData);
      }
    } catch (err) {
      if (handle422Redirect(err)) return;

      // On 400, Kratos returns the updated flow with error messages in ui.messages/nodes
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { status?: number; data?: LoginFlow & { error?: { message?: string } } } }).response;
        if (resp?.status === 400 && resp.data?.ui) {
          // Update flow to show Kratos error messages via FlowMessages
          setFlow(resp.data as LoginFlow);
          setError(null); // Let FlowMessages handle it
          setIsSubmitting(false);
          return;
        }
      }

      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: WebAuthn — passkey ceremony
  // ============================================================

  const handlePasskeyLogin = async () => {
    if (!flow) return;
    setIsSubmitting(true);
    setError(null);

    const webauthnNodes = flow.ui.nodes.filter((n) => n.group === 'webauthn');
    const loginTrigger = webauthnNodes.find((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'webauthn_login_trigger';
    });
    const triggerAttrs = loginTrigger?.attributes as unknown as { onclick?: string } | undefined;
    const onclickCode = triggerAttrs?.onclick || '';
    const optMatch = onclickCode.match(/window\.__oryWebAuthnLogin\((.+)\)/s);

    if (!optMatch) {
      setError('WebAuthn not available');
      setIsSubmitting(false);
      return;
    }

    try {
      // eslint-disable-next-line no-eval
      const opt = eval(`(${optMatch[1]})`);
      const b64Decode = (v: string) =>
        Uint8Array.from(atob(v.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      const b64Encode = (v: ArrayBuffer) =>
        btoa(String.fromCharCode(...new Uint8Array(v)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

      opt.publicKey.challenge = b64Decode(opt.publicKey.challenge);
      if (opt.publicKey.allowCredentials) {
        opt.publicKey.allowCredentials = opt.publicKey.allowCredentials.map(
          (c: { id: string }) => ({ ...c, id: b64Decode(c.id) })
        );
      }

      const credential = await navigator.credentials.get(opt) as PublicKeyCredential;
      const response = credential.response as AuthenticatorAssertionResponse;

      const webauthnLogin = JSON.stringify({
        id: credential.id,
        rawId: b64Encode(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: b64Encode(response.authenticatorData),
          clientDataJSON: b64Encode(response.clientDataJSON),
          signature: b64Encode(response.signature),
          userHandle: b64Encode(response.userHandle || new ArrayBuffer(0)),
        },
      });

      // Build a native form and submit (browser flow)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = flow.ui.action;
      form.style.display = 'none';

      // Add hidden fields
      const addField = (name: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      // CSRF + default hidden fields
      for (const node of flow.ui.nodes) {
        if (node.group === 'default' && (node.attributes as { type?: string }).type === 'hidden') {
          const a = node.attributes as UiNodeInputAttributes;
          addField(a.name, a.value as string);
        }
      }

      addField('webauthn_login', webauthnLogin);

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError(handleKratosError(err));
      }
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: TOTP
  // ============================================================

  const handleTOTPSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'totp',
          totp_code: totpCode,
          csrf_token: getCsrfToken(),
        } as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });
      if (data.session) {
        await handleLoginSuccess(data);
      }
    } catch (err) {
      setError(handleKratosError(err));
      if (flow) {
        try {
          const { data } = await kratos.getLoginFlow({ id: flow.id });
          setFlow(data);
        } catch { /* ignore */ }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: Backup codes
  // ============================================================

  const handleLookupSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'lookup_secret',
          lookup_secret: lookupCode,
          csrf_token: getCsrfToken(),
        } as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });
      if (data.session) {
        await handleLoginSuccess(data);
      }
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  const defaultNodes = flow?.ui.nodes.filter((n) => n.group === 'default') || [];
  const passwordNodes = flow?.ui.nodes.filter((n) => n.group === 'password') || [];

  const Header = () => (
    <div className="text-center mb-8">
      <img src="/orbital-passport-lm.svg" alt="OrbitalPassport" className="h-16 w-16 mx-auto mb-4 dark:hidden" />
      <img src="/orbital-passport-transparent.svg" alt="OrbitalPassport" className="h-16 w-16 mx-auto mb-4 hidden dark:block" />
      <h1 className="text-2xl font-bold text-content-primary">Welcome Back</h1>
      <p className="text-content-secondary mt-1">Sign in to your account</p>
    </div>
  );

  const ErrorBanner = () => error ? (
    <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
      {error}
    </div>
  ) : null;

  const RegisterLinks = () => (
    <div className="mt-6 text-center space-y-2">
      <p className="text-content-secondary">Don&apos;t have an account?</p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <Link to={`/register/personal?return_to=${encodeURIComponent(returnTo)}`}
          className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium text-sm">
          Register as individual
        </Link>
        <span className="hidden sm:inline text-content-tertiary">|</span>
        <Link to={`/register/business?return_to=${encodeURIComponent(returnTo)}`}
          className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium text-sm">
          Register as business
        </Link>
      </div>
    </div>
  );

  // ── TOTP View ──
  if (loginPhase === 'totp') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-orbital-100 dark:bg-orbital-900 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orbital-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content-primary">Two-Factor Authentication</h1>
            <p className="text-content-secondary mt-1">Enter the code from your authenticator app</p>
          </div>
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />
          <form onSubmit={handleTOTPSubmit} className="space-y-4">
            <input id="totp-code" type="text" inputMode="numeric" autoComplete="one-time-code"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="block w-full text-center text-3xl tracking-[0.5em] font-mono px-4 py-4 border border-border rounded-lg focus:ring-orbital-500 focus:border-orbital-500"
              maxLength={6} disabled={isSubmitting} autoFocus />
            <button type="submit" disabled={isSubmitting || totpCode.length !== 6}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <button onClick={() => setLoginPhase('lookup_secret')}
              className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
              Use a backup code instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Backup Code View ──
  if (loginPhase === 'lookup_secret') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content-primary">Use Backup Code</h1>
            <p className="text-content-secondary mt-1">Enter one of your recovery codes</p>
          </div>
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />
          <form onSubmit={handleLookupSubmit} className="space-y-4">
            <input type="text" value={lookupCode} onChange={(e) => setLookupCode(e.target.value)}
              placeholder="Enter backup code"
              className="block w-full text-center font-mono px-4 py-3 border border-border rounded-lg focus:ring-orbital-500 focus:border-orbital-500"
              disabled={isSubmitting} autoFocus />
            <button type="submit" disabled={isSubmitting || !lookupCode.trim()}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <button onClick={() => setLoginPhase('totp')}
              className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
              Use authenticator app instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── WebAuthn View (passkey challenge ready) ──
  if (loginPhase === 'webauthn') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <Header />
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />

          <div className="space-y-4">
            <p className="text-sm text-content-secondary text-center">
              A passkey is available for <strong>{identifier}</strong>
            </p>

            <button
              type="button"
              disabled={isSubmitting}
              onClick={handlePasskeyLogin}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-orbital-600 text-white rounded-lg font-medium hover:bg-orbital-700 transition-colors"
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  Sign in with Passkey
                </>
              )}
            </button>

            <div className="text-center">
              <button onClick={async () => {
                // Create a fresh flow for password login — the webauthn flow doesn't have password fields
                setError(null);
                try {
                  const { data } = await kratos.createBrowserLoginFlow({ returnTo });
                  const newParams = new URLSearchParams();
                  newParams.set('flow', data.id);
                  if (isMobileLogin && mobileCallback) {
                    newParams.set('mobile', 'true');
                    newParams.set('callback', mobileCallback);
                  }
                  navigate(`/login?${newParams.toString()}`, { replace: true });
                  setFlow(data);
                  setLoginPhase('password');
                } catch {
                  setLoginPhase('password');
                }
              }}
                className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                Use password instead
              </button>
            </div>
          </div>
          <RegisterLinks />
        </div>
      </div>
    );
  }

  // ── QR Login success handler ──
  const handleQRSuccess = (userEmail?: string) => {
    void userEmail;
    // Session cookie was set by the gateway status endpoint.
    // Just redirect to the return URL.
    window.location.href = returnTo;
  };

  // ── Identifier View (email first) with QR tab ──
  if (loginPhase === 'identifier') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <Header />

          {/* Login method tabs — only show on non-mobile flows */}
          {!isMobileLogin && (
            <div className="flex border-b border-border mb-6">
              <button
                onClick={() => setLoginTab('credentials')}
                className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  loginTab === 'credentials'
                    ? 'border-orbital-600 text-orbital-600'
                    : 'border-transparent text-content-tertiary hover:text-content-secondary'
                }`}
              >
                Email / Password
              </button>
              <button
                onClick={() => setLoginTab('qr')}
                className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  loginTab === 'qr'
                    ? 'border-orbital-600 text-orbital-600'
                    : 'border-transparent text-content-tertiary hover:text-content-secondary'
                }`}
              >
                QR Code
              </button>
            </div>
          )}

          {loginTab === 'qr' && !isMobileLogin ? (
            <QRLogin onSuccess={handleQRSuccess} returnTo={returnTo} />
          ) : (
            <>
              <FlowMessages messages={flow?.ui.messages} />
              <ErrorBanner />

              <form onSubmit={handleIdentifierSubmit} className="space-y-4">
                {/* Hidden fields */}
                {defaultNodes
                  .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                  .map((node, i) => (
                    <FormField key={i} node={node} disabled={isSubmitting} />
                  ))}

                {/* Email field */}
                <div className="space-y-1">
                  <label htmlFor="identifier" className="block text-sm font-medium text-content-primary">Email</label>
                  <input
                    id="identifier"
                    name="identifier"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    disabled={isSubmitting}
                    className="input-field"
                  />
                </div>

                <button type="submit" disabled={isSubmitting || !identifier.trim()}
                  className="btn-primary w-full flex items-center justify-center">
                  {isSubmitting ? <LoadingSpinner size="sm" /> : 'Continue'}
                </button>
              </form>

              <div className="mt-3 text-center">
                <button onClick={() => { setLoginPhase('password'); }}
                  className="text-sm text-content-tertiary hover:text-content-secondary">
                  Sign in with password directly
                </button>
              </div>
            </>
          )}

          <RegisterLinks />
        </div>
      </div>
    );
  }

  // ── Password View ──
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <Header />

        {/* Login method tabs — only show on non-mobile flows */}
        {!isMobileLogin && (
          <div className="flex border-b border-border mb-6">
            <button
              onClick={() => setLoginTab('credentials')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                loginTab === 'credentials'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-secondary'
              }`}
            >
              Email / Password
            </button>
            <button
              onClick={() => setLoginTab('qr')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                loginTab === 'qr'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-secondary'
              }`}
            >
              QR Code
            </button>
          </div>
        )}

        {loginTab === 'qr' && !isMobileLogin ? (
          <QRLogin onSuccess={handleQRSuccess} returnTo={returnTo} />
        ) : (
          <>
            <FlowMessages messages={flow?.ui.messages} />
            <ErrorBanner />

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Hidden fields */}
              {defaultNodes
                .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              {/* Identifier — show if not already entered */}
              {identifier ? (
                <div className="flex items-center justify-between bg-surface-secondary rounded-lg px-4 py-3">
                  <div>
                    <span className="text-sm text-content-tertiary">Signing in as</span>
                    <p className="text-sm font-medium text-content-primary">{identifier}</p>
                  </div>
                  <button type="button" onClick={() => { setIdentifier(''); setLoginPhase('identifier'); setError(null); }}
                    className="text-sm text-orbital-600 hover:text-orbital-800">Change</button>
                  <input type="hidden" name="identifier" value={identifier} />
                </div>
              ) : (
                defaultNodes
                  .filter((n) => (n.attributes as { type?: string }).type !== 'hidden')
                  .map((node, i) => (
                    <FormField key={i} node={node} disabled={isSubmitting} />
                  ))
              )}

              {/* Password field */}
              {passwordNodes
                .filter((n) => (n.attributes as { type?: string }).type !== 'submit')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              <input type="hidden" name="method" value="password" />

              <div className="text-right">
                <Link to="/recovery"
                  className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center">
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Sign In'}
              </button>
            </form>

            {/* Switch to passkey if available */}
            {hasPasskey && (
              <div className="mt-3 text-center">
                <button onClick={() => setLoginPhase('webauthn')}
                  className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                  Use passkey instead
                </button>
              </div>
            )}
          </>
        )}

        <RegisterLinks />
      </div>
    </div>
  );
}
